import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { TensionLogo } from '../../app/components/TensionLogo';

interface MainNavigationProps {
  onSignIn?: () => void;
  onGetStarted?: () => void;
}

const NAV_LINKS = [
  { label: 'Product',  href: '#product'  },
  { label: 'Features', href: '#features' },
  { label: 'Insights', href: '#insights' },
  { label: 'Pricing',  href: '/pricing'  },
  { label: 'About',    href: '#about'    },
];

export function MainNavigation({ onSignIn, onGetStarted }: MainNavigationProps) {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  /* ─ Scroll-aware visibility ─ */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      if (y > lastScrollY.current + 8 && y > 120) setHidden(true);
      else if (y < lastScrollY.current - 4) setHidden(false);
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleGetStarted = onGetStarted ?? (() => navigate('/auth?signup=true'));
  const handleSignIn     = onSignIn     ?? (() => navigate('/auth'));

  const handleLinkClick = useCallback((href: string) => {
    setMobileOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(href);
    }
  }, [navigate]);

  return (
    <>
      <motion.header
        className="fixed left-0 right-0 z-50 flex justify-center"
        style={{ top: 22 }}
        animate={{
          y:       hidden ? -120 : 0,
          opacity: hidden ? 0    : 1,
        }}
        transition={prefersReducedMotion ? { duration: 0 } : {
          duration: 0.44,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {/* ── Desktop navigation capsule ── */}
        <div
          className="relative hidden h-[76px] items-center justify-between md:flex"
          style={{
            width: 'min(92vw, 1120px)',
            paddingInline: 34,
            backgroundImage: 'url(/assets/tension/ui/navigation-glass-bar.png)',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            /* Premium glass — deeper when scrolled */
            backgroundColor: scrolled
              ? 'rgba(3, 7, 18, 0.88)'
              : 'rgba(5, 10, 24, 0.60)',
            borderRadius: 999,
            backdropFilter: 'blur(28px) saturate(1.3)',
            WebkitBackdropFilter: 'blur(28px) saturate(1.3)',
            /* Layered border system for physical glass realism */
            boxShadow: scrolled
              ? '0 1px 0 rgba(255,255,255,.09) inset, 0 0 0 1px rgba(255,255,255,.06), 0 20px 48px rgba(0,0,0,.45)'
              : '0 1px 0 rgba(255,255,255,.07) inset, 0 0 0 1px rgba(255,255,255,.05), 0 12px 32px rgba(0,0,0,.32)',
            transition: 'background-color .35s ease, box-shadow .35s ease',
          }}
        >
          {/* Top specular light line */}
          <div
            className="pointer-events-none absolute inset-x-8 top-0 h-px rounded-full"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.18) 40%, rgba(255,255,255,.18) 60%, transparent)',
              opacity: scrolled ? 0.7 : 0.5,
              transition: 'opacity .35s ease',
            }}
          />

          {/* ── Left: Logo ── */}
          <Link to="/" aria-label="TENSION home" className="relative z-10 flex shrink-0 items-center">
            <TensionLogo
              tone="light"
              height={44}
              priority
              className="max-w-[190px]"
            />
          </Link>

          {/* ── Center: Links ── */}
          <nav
            className="relative z-10 flex items-center gap-2"
            aria-label="Primary navigation"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.label}
                label={link.label}
                href={link.href}
                onClick={() => handleLinkClick(link.href)}
              />
            ))}
          </nav>

          {/* ── Right: Actions ── */}
          <div className="relative z-10 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSignIn}
              className="rounded-xl px-4 py-2 text-[13px] font-medium transition-colors duration-200 hover:bg-white/[0.05] hover:text-white"
              style={{ color: 'rgba(244,246,252,.68)' }}
            >
              Sign In
            </button>
            <motion.button
              type="button"
              onClick={handleGetStarted}
              className="t-btn-primary relative flex items-center gap-1.5"
              style={{ borderRadius: 13, padding: '9px 20px', fontSize: 13, fontWeight: 600 }}
              whileHover={prefersReducedMotion ? undefined : { scale: 1.03, y: -1 }}
              whileTap={prefersReducedMotion ? undefined : { scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            >
              Get Started
              <ArrowRight className="h-3 w-3 stroke-[2.5]" />
            </motion.button>
          </div>
        </div>

        {/* ── Mobile nav bar ── */}
        <div
          className="flex h-[60px] w-[calc(100vw-28px)] items-center justify-between px-4 md:hidden"
          style={{
            backgroundColor: 'rgba(4, 8, 22, 0.88)',
            border: '1px solid rgba(255,255,255,.08)',
            borderRadius: 20,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          <Link to="/" aria-label="TENSION home">
            <TensionLogo tone="light" height={30} priority className="max-w-[120px]" />
          </Link>
          <button
            type="button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </motion.header>

      {/* ── Mobile fullscreen menu ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-40 flex flex-col items-center justify-center md:hidden"
            style={{ backgroundColor: 'rgba(4, 8, 22, 0.97)' }}
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(28px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.30, ease: [0.22, 1, 0.36, 1] }}
          >
            <nav className="flex flex-col items-center gap-3">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.label}
                  type="button"
                  className="text-[28px] font-bold text-white/88 transition-colors hover:text-white"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.30, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => handleLinkClick(link.href)}
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.button
                type="button"
                className="t-btn-primary mt-6 w-full max-w-[260px] text-base"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: NAV_LINKS.length * 0.05 + 0.05, duration: 0.30 }}
                onClick={() => { setMobileOpen(false); handleGetStarted(); }}
              >
                Start Your Journey
              </motion.button>
              <motion.button
                type="button"
                className="mt-2 text-sm text-white/55 hover:text-white/80"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                onClick={() => { setMobileOpen(false); handleSignIn(); }}
              >
                Sign In
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ── Nav link with animated underline glow ── */
function NavLink({ label, href, onClick }: { label: string; href: string; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      className="relative px-5 py-2.5 text-[13px] font-medium tracking-[0.01em]"
      style={{ color: hovered ? '#ffffff' : 'rgba(244,246,252,.68)' }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {label}
      <AnimatePresence>
        {hovered && (
          <motion.span
            className="absolute bottom-0.5 left-3 right-3 h-px rounded-full"
            style={{
              background: 'linear-gradient(90deg, #FF1FBF, #A855F7, #00B2FF)',
              boxShadow: '0 0 8px rgba(168,85,247,.6)',
            }}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </AnimatePresence>
    </button>
  );
}
